import { useState, useEffect, type ReactNode } from 'react'
import { createFileRoute, Link } from '@tanstack/react-router'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { XCircle, Loader2 } from 'lucide-react'
import { BrowserCheckCta } from '@/components/browser-check-cta'
import { LocationResultList } from '@/components/location-result-list'
import { MonitorSiteCta } from '@/components/monitor-site-cta'
import { WebsiteCheckVerdict } from '@/components/website-check-verdict'
import { useWebsiteCheck } from '@/hooks/use-website-check'
import { rowsOf } from '@/lib/website-check-stream'
import { sitesByCategory } from '@/lib/down-sites'
import { EDGE_LOCATION_COUNT } from '@/lib/check-locations'

export const Route = createFileRoute('/_site/tools/is-website-down')({
  component: IsWebsiteDownPage,
  // Never inject a default: the router redirects (307) any URL whose validated search differs from
  // the request, so a default `check: ''` sent the bare URL to `?check=` for every visitor and crawler.
  // Only a string counts (`?check=123` parses as a number); an explicit `?check=` stays as typed.
  validateSearch: (search: Record<string, unknown>): { check?: string } => ({
    check: typeof search.check === 'string' ? search.check : undefined,
  }),
  head: () => ({
    meta: [
      { title: 'Is This Website Down? Free Multi-Region Checker — Uptrack' },
      { name: 'description', content: `Check if a website is down from ${EDGE_LOCATION_COUNT} locations on six continents. Free and instant. See per-region response times and status codes.` },
      { property: 'og:title', content: 'Is This Website Down? — Free Multi-Region Checker' },
      { property: 'og:description', content: `Check if a website is down from ${EDGE_LOCATION_COUNT} locations on six continents. Free, instant, no signup required.` },
      { property: 'og:image', content: 'https://uptrack.app/og/tools-is-website-down.png' },
    ],
    links: [
      { rel: 'canonical', href: 'https://uptrack.app/tools/is-website-down' },
    ],
  }),
})

function IsWebsiteDownPage() {
  const { check: autoCheck = '' } = Route.useSearch()
  const [url, setUrl] = useState(autoCheck || '')

  const { run, check } = useWebsiteCheck()
  const running = run.status === 'running'

  // A new ?check= (a popular-site link) also fills the input.
  const [shownCheck, setShownCheck] = useState(autoCheck)
  if (autoCheck !== shownCheck) {
    setShownCheck(autoCheck)
    if (autoCheck) setUrl(autoCheck)
  }

  // Check whenever ?check= changes (on load and from the popular-site links).
  useEffect(() => {
    if (autoCheck) check(autoCheck)
  }, [autoCheck, check])

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    if (!url.trim()) return
    check(url.trim())
  }

  return (
    <>

      <>
        {/* Hero */}
        <section className="container mx-auto px-4 pb-8 pt-12 md:pt-16">
          <div className="max-w-3xl">
            <h1 className="text-[2.25rem] font-semibold leading-[1.1] tracking-tight md:text-[3rem]">Is this website down?</h1>
            <p className="mt-4 max-w-2xl text-lead text-body">
              Check from {EDGE_LOCATION_COUNT} locations on six continents instantly. Free, no signup required.
            </p>

            {/* URL Input */}
            <form onSubmit={handleSubmit} className="mt-8 flex max-w-xl gap-2">
              <Input
                type="text"
                aria-label="Website address"
                placeholder="example.com"
                value={url}
                onChange={(e) => setUrl(e.target.value)}
                className="h-12 text-base"
                autoFocus
              />
              <Button type="submit" size="lg" className="h-12 px-6" disabled={running}>
                {running ? <Loader2 className="h-5 w-5 animate-spin" /> : 'Check'}
              </Button>
            </form>
          </div>
        </section>

        {/* Results: every location at once, each row filled in as it answers */}
        {(running || run.status === 'done') && (
          <section className="py-12">
            <div className="container mx-auto px-4">
              <WebsiteCheckVerdict run={run} name={run.url ?? url} className="mb-8" />
              {run.note && <p className="mb-4 max-w-3xl text-sm text-muted-foreground">{run.note}</p>}
              <LocationResultList results={rowsOf(run)} />

              {/* CTA: monitor this exact site */}
              {run.status === 'done' && run.url && <MonitorSiteCta url={run.url} className="mt-10" />}
            </div>
          </section>
        )}

        {/* Error */}
        {run.status === 'error' && (
          <section className="py-12">
            <div className="container mx-auto max-w-3xl px-4">
              <div className="rounded-lg border border-status-down bg-status-down-soft p-6">
                <XCircle className="mb-3 h-8 w-8 text-status-down" />
                <p className="text-status-down">{run.error || 'Failed to check website. Please try again.'}</p>
              </div>
            </div>
          </section>
        )}

        {/* FAQ for SEO */}
        <section className="border-y border-border bg-card">
          <div className="container mx-auto px-4 py-12 md:py-16">
            <h2 className="mb-8 text-2xl font-semibold tracking-tight md:text-3xl">Frequently Asked Questions</h2>
            <div className="max-w-3xl divide-y divide-border border-y border-border">
              <FaqItem
                q="How does this work?"
                a="We send an HTTP request to the website from 24 locations on six continents: Europe, North and South America, Asia, Australia and Africa. If the site responds with a successful status code, it's up. If it doesn't respond or returns an error, it's down."
              />
              <FaqItem
                q="Why check from multiple locations?"
                a={<>A website might be down in one region but up in others. CDN issues, DNS propagation delays, or regional outages can make a site unreachable from specific locations. Checking from 24 locations gives you the full picture. Read the guide: <Link to="/blog/how-to-check-if-a-website-is-down-from-multiple-locations" className="font-medium underline">how to check if a website is down from multiple locations</Link>.</>}
              />
              <FaqItem
                q="Is this free?"
                a="Yes, completely free with no signup required. Checks are spaced a few seconds apart for each visitor, so one person cannot overload a site."
              />
              <FaqItem
                q="What if only one region shows 'down'?"
                a="That usually means a regional issue — like a CDN edge server being down, or a routing problem between that region and the server. The website itself is probably fine. This is exactly the kind of false alert that Uptrack's multi-region consensus prevents."
              />
              <FaqItem
                q="How is this different from Uptrack monitoring?"
                a="This tool is a one-time check. Uptrack monitoring checks your site every 30 seconds, continuously, and alerts you via Slack, email, or Telegram when it goes down. Pro includes 50 monitors at 30-second intervals."
              />
            </div>
          </div>
        </section>

        {/* Hub: every service with its own page. Plain links only; nothing here runs a check. */}
        <section id="services" className="scroll-mt-20">
          <div className="container mx-auto px-4 py-12 md:py-16">
            <h2 className="mb-2 text-2xl font-semibold tracking-tight md:text-3xl">Check a popular service</h2>
            <p className="mb-8 max-w-2xl text-body">
              Each service has its own page. It checks the service from 24 locations and links to the official status
              page when there is one.
            </p>
            <div className="columns-1 gap-x-10 sm:columns-2 lg:columns-4">
              {sitesByCategory().map(({ category, sites }) => (
                <div key={category} className="mb-8 break-inside-avoid">
                  <h3 className="mb-2 text-sm font-semibold text-foreground">{category}</h3>
                  <ul className="space-y-1">
                    {sites.map((site) => (
                      <li key={site.domain}>
                        <Link
                          to="/tools/is-website-down/$domain"
                          params={{ domain: site.domain }}
                          className="text-sm text-body transition-colors hover:text-primary hover:underline"
                        >
                          {site.name}
                        </Link>
                      </li>
                    ))}
                  </ul>
                </div>
              ))}
            </div>
          </div>
        </section>

        <section className="mx-auto max-w-3xl px-4 pb-16">
          <BrowserCheckCta
            heading="Down or just slow? Run a full browser check"
            sub="See a real-browser screenshot plus Core Web Vitals (LCP, CLS, FCP, TTFB) — find out whether the page renders and how fast, not just whether it responds."
          />
        </section>
      </>

      </>
  )
}

function FaqItem({ q, a }: { q: string; a: ReactNode }) {
  return (
    <div className="py-5">
      <h3 className="mb-2 font-semibold text-foreground">{q}</h3>
      <p className="text-[15px] leading-relaxed text-body">{a}</p>
    </div>
  )
}
